<script setup lang="ts">
import { OnClickOutside } from "@vueuse/components";
import type { InputSelectOption, InputSelectOptionSettings } from "shared/interfaces";
import type { PropType } from "vue";
import { capitalize } from "vue";

const props = defineProps({
	type: {
		type: String,
		default: "text"
	},
	title: {
		type: String,
		default: "Title"
	},
	placeholder: {
		type: String,
		default: "Placeholder"
	},
	error: {
		type: String,
		default: ""
	},
	required: {
		type: Boolean,
		default: false
	},
	optionsPrimaryKey: {
		type: String,
		required: true
	},
	options: {
		type: Array as PropType<InputSelectOption[]>,
		default () {
			return [];
		}
	},
	optionsSettings: {
		type: Object as PropType<InputSelectOptionSettings>,
		default () {
			return {
				searchOptionsByInput: true
			};
		}
	},
	modelValue: {
		type: Object as PropType<InputSelectOption>,
		default () {
			return {
				id: ""
			};
		}
	}
});
const emit = defineEmits(["update:modelValue"]);

const id = useId();
const isFocused = ref(false);
const inputValue = ref(props.modelValue[props.optionsPrimaryKey]);

const modelValueAccessor = computed({
	get () {
		return props.modelValue;
	},
	set (value) {
		emit("update:modelValue", value);
	}
});
const filteredOptions = computed(() => props.optionsSettings.searchOptionsByInput ? searchOptionsByInput(props.options, inputValue.value) : props.options);
const focusedClasses = computed(() => isFocused.value && "elevation-cta-shadow-border");
const errorClasses = computed(() => !isFocused.value && !!props.error && "border-rose-500");

const getOptionEqualsInput = (options: InputSelectOption[], input = "") => {
	const inputSanitized = input.toLowerCase().trim();

	return options.find(option => option[props.optionsPrimaryKey].toLowerCase() === inputSanitized);
};
const searchOptionsByInput = (options: InputSelectOption[], input = "") => {
	return options.filter((option) => {
		const optionSanitized = option[props.optionsPrimaryKey].toLowerCase();
		const inputSanitized = input.toLowerCase().trim();

		return optionSanitized.search(inputSanitized) !== -1;
	});
};
const selectOption = (option: InputSelectOption = { id: 0 }) => {
	inputValue.value = Object.values(option).length ? option[props.optionsPrimaryKey] : "";
	modelValueAccessor.value = option;
};
const selectOptionIfValid = () => {
	const validOption = getOptionEqualsInput(props.options, inputValue.value);
	selectOption(validOption);
};
const autoComplete = () => {
	if (!inputValue.value) {
		selectOption();
	} else {
		const [suitableOption] = filteredOptions.value;
		selectOption(suitableOption);
	}
};
const onBlur = () => {
	if (isFocused.value) {
		selectOptionIfValid();
		isFocused.value = false;
	}
};

watch(() => props.modelValue[props.optionsPrimaryKey], (newValue) => {
	inputValue.value = newValue;
});
</script>

<template>
	<OnClickOutside class="relative w-full" @trigger="onBlur">
		<div>
			<div
				class="flex flex-col p-3 bg-gray-50 border border-gray-200 rounded-sm duration-75"
				:class="[focusedClasses, errorClasses]"
			>
				<div class="flex justify-between">
					<label :for="id" class="font-semibold text-gray-500 text-sm">
						<span>{{ title }}</span>
						<span v-if="required" class="text-rose-500">*</span>
					</label>
					<NuxtIcon name="arrow-down-16" class="text-gray-500" />
				</div>
				<input
					:id="id"
					ref="input"
					v-model="inputValue"
					:type="type"
					class="mt-1 text-base text-gray-900 focus-visible:outline-none bg-transparent"
					:placeholder="placeholder"
					@keydown.enter="autoComplete()"
					@keydown.tab="autoComplete(), isFocused = false"
					@focusin="isFocused = true"
				/>
			</div>
			<div v-show="error" class="font-semibold text-sm text-rose-500 mt-1">
				{{ capitalize(error) }}
			</div>
			<AppDropdown v-if="isFocused && filteredOptions.length" class="mt-1 w-full">
				<AppButton
					v-for="(option, i) in filteredOptions"
					:key="i"
					tag="li"
					tabindex="-1"
					@click.prevent="selectOption(option), isFocused = false"
				>
					{{ option[optionsPrimaryKey] }}
				</AppButton>
			</AppDropdown>
		</div>
	</OnClickOutside>
</template>
